import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getInvoice, updateInvoiceDraft, finalizeInvoice, deleteInvoiceDraft, listInvoiceCreditNotes, openInvoicePdf, downloadInvoicePdf } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Save, CheckCircle2, Lock, FileText, FileDown, Loader2, AlertCircle, Trash2, RotateCcw } from "lucide-react";
import InvoiceLinesEditor from "../components/facturen/InvoiceLinesEditor";
import CreditnotaVanuitFactuurDialog from "../components/facturen/CreditnotaVanuitFactuurDialog";
import { TAX_REGIME_LABELS, TAX_REGIME_KEYS, TAX_REGIME_HINTS } from "../lib/taxRegimes";

const STATUS_LABEL = {
  draft: "Concept",
  definitive: "Definitief",
  sent: "Verzonden",
  paid: "Betaald",
  cancelled: "Geannuleerd",
};
const STATUS_TONE = {
  draft:      "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  definitive: "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  sent:       "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  paid:       "bg-[#FAF9F6] text-[#2C2C2A] border-[#E5E2DC]",
  cancelled:  "bg-[#FAF9F6] text-[#A8A7A0] border-[#E5E2DC]",
};
const TYPE_LABEL = {
  deposit: "Voorschot",
  interim: "Tussenfactuur",
  final: "Eindfactuur",
  free: "Vrije factuur",
};

const fmtEur = (n) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" })
    .format(Number(n || 0));

export default function FactuurDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [regime, setRegime] = useState("standard");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // v14v Sprint 2B — Creditnota-dialog + gekoppelde CN's op deze factuur.
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);
  const [linkedCreditNotes, setLinkedCreditNotes] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getInvoice(id);
      setInv(data);
      setLines(data.line_items || []);
      setNotes(data.notes || "");
      setRegime(data.tax_regime_key || "standard");
      // Alleen op een originele factuur (doc_kind=invoice) heeft het zin om
      // gekoppelde creditnota's op te halen. Faal stilzwijgend — dit is enkel
      // een informatieve badge.
      if ((data.doc_kind || "invoice") === "invoice") {
        try {
          const cns = await listInvoiceCreditNotes(id);
          setLinkedCreditNotes(cns || []);
        } catch {
          setLinkedCreditNotes([]);
        }
      } else {
        setLinkedCreditNotes([]);
      }
    } catch {
      toast.error("Factuur niet gevonden.");
      navigate("/facturen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isDraft = inv?.status === "draft";
  const isCancelled = inv?.status === "cancelled";
  // v14v Sprint 2B — Doc kind afgeleiden.
  const docKind = inv?.doc_kind || "invoice";
  const isCreditNote = docKind === "credit_note";
  const isProForma = docKind === "pro_forma";
  const canCreateCreditNote =
    !!inv && docKind === "invoice" &&
    ["definitive", "sent", "paid"].includes(inv?.status);

  // Live totalen (client-side spiegel — backend is autoritatief).
  // v14r — reverse_charge: BTW-buckets → 0, total_incl == subtotal (matcht backend).
  const totals = useMemo(() => {
    const buckets = {};
    let sub = 0;
    for (const ln of lines || []) {
      const raw = Number(ln.quantity || 0) * Number(ln.unit_price_excl || 0);
      const excl = ln.line_type === "discount" ? -Math.abs(raw) : raw;
      if (ln.line_type === "option") continue;
      sub += excl;
      const vr = Number(ln.vat_rate || 0);
      buckets[vr] = (buckets[vr] || 0) + excl * vr / 100;
    }
    if (regime === "reverse_charge") {
      return {
        subtotal: Math.round(sub * 100) / 100,
        buckets: {},
        total_vat: 0,
        total_incl: Math.round(sub * 100) / 100,
      };
    }
    const totalVat = Object.values(buckets).reduce((a, b) => a + b, 0);
    return {
      subtotal: Math.round(sub * 100) / 100,
      buckets: Object.fromEntries(Object.entries(buckets).map(([k, v]) => [k, Math.round(v * 100) / 100])),
      total_vat: Math.round(totalVat * 100) / 100,
      total_incl: Math.round((sub + totalVat) * 100) / 100,
    };
  }, [lines, regime]);

  // v14r — Regime live wijzigen: backend overschrijft alle line vat_rates.
  const onRegimeChange = async (newRegime) => {
    if (!isDraft || newRegime === regime) return;
    setRegime(newRegime); // optimistisch — UI wisselt onmiddellijk
    try {
      const updated = await updateInvoiceDraft(id, { tax_regime_key: newRegime });
      setInv(updated);
      setLines(updated.line_items || []);
      setRegime(updated.tax_regime_key || newRegime);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "BTW-regime wijzigen mislukt.");
      setRegime(regime); // rollback
    }
  };

  // v14r — Mismatch: bewuste afwijking van offerte-BTW → informatieve banner.
  const quoteVat = inv?.quote_vat_rate_snapshot;
  const effectiveRate = regime === "reverse_charge" ? 0 : regime === "reduced_renovation" ? 6 : 21;
  const showRegimeMismatch =
    isDraft && !!inv?.quote_id && typeof quoteVat === "number" && effectiveRate !== quoteVat;

  // v14r — Concept verwijderen (hard delete, alleen drafts).
  const [deleting, setDeleting] = useState(false);
  const onDelete = async () => {
    setDeleting(true);
    try {
      await deleteInvoiceDraft(id);
      toast.success("Conceptfactuur verwijderd.");
      navigate("/facturen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt.");
      setDeleting(false);
    }
  };

  const onSave = async () => {
    if (!isDraft) return;
    setSaving(true);
    try {
      const updated = await updateInvoiceDraft(id, {
        line_items: lines, notes, tax_regime_key: regime,
      });
      setInv(updated);
      setLines(updated.line_items || []);
      setRegime(updated.tax_regime_key || "standard");
      toast.success("Wijzigingen bewaard.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bewaren mislukt.");
    } finally {
      setSaving(false);
    }
  };

  const onFinalize = async () => {
    setFinalizing(true);
    try {
      // Bewaar eerst laatste edits.
      if (isDraft) {
        await updateInvoiceDraft(id, {
          line_items: lines, notes, tax_regime_key: regime,
        });
      }
      const updated = await finalizeInvoice(id);
      setInv(updated);
      setLines(updated.line_items || []);
      setRegime(updated.tax_regime_key || "standard");
      toast.success(`Factuur ${updated.invoice_number} definitief.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Definitief maken mislukt.");
    } finally {
      setFinalizing(false);
    }
  };

  if (loading || !inv) {
    return (
      <div className="flex items-center justify-center py-20 text-[#4F4F4D]" data-testid="factuur-detail-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Factuur laden…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="factuur-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <Button asChild variant="ghost" size="sm" className="h-8 px-2 -ml-2 text-[#4F4F4D] hover:bg-[#F0EFEA]">
            <Link to="/facturen"><ArrowLeft className="w-4 h-4 mr-1" /> Terug naar facturen</Link>
          </Button>
          <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold">
            {inv.doc_kind === "pro_forma" ? "Pro forma" : inv.doc_kind === "credit_note" ? "Creditnota" : (TYPE_LABEL[inv.invoice_type] || inv.invoice_type)}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] leading-tight" data-testid="factuur-detail-title">
            {inv.invoice_number || <span className="italic text-[#A8A7A0]">{inv.doc_kind === "pro_forma" ? "Pro forma — concept" : inv.doc_kind === "credit_note" ? "Creditnota — concept" : "Concept-factuur"}</span>}
          </h1>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <Badge variant="outline" className={`${STATUS_TONE[inv.status]}`} data-testid="factuur-detail-status">
              {STATUS_LABEL[inv.status]}
            </Badge>
            {/* v6 F3.5b — Regime-badge (altijd tonen). pending_verification wordt
                gehighlight in de badge zelf zodra de factuur definitief is. */}
            <Badge
              variant="outline"
              className={`bg-[#FAF9F6] border-[#E5E2DC] ${
                inv.legal_notice_snapshot?.status === "pending_verification"
                  ? "text-[#7F4124] border-[#F0C4A3] bg-[#FDF7F2]"
                  : "text-[#2C2C2A]"
              }`}
              data-testid="factuur-detail-regime"
              title={inv.legal_notice_snapshot?.status === "pending_verification"
                ? "Wettelijke tekst nog te bevestigen door accountant"
                : ""}
            >
              {TAX_REGIME_LABELS[inv.tax_regime_key] || "Standaard BTW"}
              {inv.legal_notice_snapshot?.status === "pending_verification" && " ⚠"}
            </Badge>
            {inv.quote_number_snapshot && (
              <Badge variant="outline" className="bg-[#FAF9F6] border-[#E5E2DC] text-[#2C2C2A]">
                <FileText className="w-3 h-3 mr-1" /> Offerte {inv.quote_number_snapshot}
              </Badge>
            )}
            {inv.invoice_date && (
              <span className="text-xs text-[#4F4F4D]">
                {new Date(inv.invoice_date).toLocaleDateString("nl-BE")}
              </span>
            )}
          </div>
          {/* v14v Sprint 2B — Creditnota → link naar bron-factuur */}
          {isCreditNote && inv.related_invoice_number_snapshot && (
            <div className="mt-2 text-xs text-[#4F4F4D]" data-testid="factuur-detail-credit-note-source">
              <span className="uppercase tracking-[0.12em] text-[10px] text-[#5D7259] font-semibold mr-1">
                Betreft factuur
              </span>
              {inv.related_invoice_id ? (
                <Link
                  to={`/facturen/${inv.related_invoice_id}`}
                  className="font-mono text-[#B07A4F] hover:underline"
                >
                  {inv.related_invoice_number_snapshot}
                </Link>
              ) : (
                <span className="font-mono text-[#2C2C2A]">{inv.related_invoice_number_snapshot}</span>
              )}
            </div>
          )}
          {/* v14v Sprint 2B — Op originele factuur: badges van gekoppelde creditnota's */}
          {!isCreditNote && linkedCreditNotes.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="factuur-detail-linked-credit-notes">
              <span className="uppercase tracking-[0.12em] text-[10px] text-[#B84B45] font-semibold">
                Gecrediteerd
              </span>
              {linkedCreditNotes.map((cn) => (
                <Link
                  key={cn.id}
                  to={`/facturen/${cn.id}`}
                  className="inline-flex items-center gap-1 text-xs bg-[#FDF0EE] text-[#B84B45] border border-[#E9C3C0] rounded-md px-2 py-0.5 hover:bg-[#FBE5E2] font-mono"
                  data-testid={`factuur-detail-linked-cn-${cn.id}`}
                >
                  <RotateCcw className="w-3 h-3" />
                  {cn.invoice_number || "concept"}
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="hidden md:flex gap-2 flex-wrap">
          <Button variant="outline"
            className="h-11 rounded-xl border-[#E5E2DC] text-[#2C2C2A] hover:bg-[#F0EFEA]"
            data-testid="factuur-detail-pdf-btn"
            title={isDraft ? "Concept-PDF (met watermerk)" : "Definitieve factuur-PDF"}
            onClick={async () => {
              try { await openInvoicePdf(inv.id); }
              catch (e) {
                const s = e?.response?.status;
                if (s === 401) toast.error("Sessie verlopen. Log opnieuw in.");
                else if (s === 404) toast.error("Factuur niet gevonden.");
                else toast.error("PDF kon niet worden geopend.");
              }
            }}
          >
            <FileDown className="w-4 h-4 mr-1.5" /> PDF
          </Button>
          <Button variant="outline"
            className="h-11 rounded-xl border-[#E5E2DC] text-[#2C2C2A] hover:bg-[#F0EFEA]"
            data-testid="factuur-detail-download-btn"
            title="PDF rechtstreeks downloaden"
            onClick={() => {
              try { downloadInvoicePdf(inv.id); }
              catch (e) { toast.error("Download mislukt. Probeer opnieuw."); }
            }}
          >
            <FileDown className="w-4 h-4 mr-1.5" /> Download
          </Button>
          {isDraft && (
            <>
              <Button
                onClick={onSave} disabled={saving}
                variant="outline"
                className="h-11 rounded-xl border-[#E5E2DC] text-[#2C2C2A] hover:bg-[#F0EFEA]"
                data-testid="factuur-detail-save"
              >
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Bezig…" : "Bewaar concept"}
              </Button>
              {/* v14r — Concept verwijderen (rood, secundair) */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-11 rounded-xl border-[#E9C3C0] text-[#B84B45] hover:bg-[#FDF0EE]"
                    data-testid="factuur-detail-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> Concept verwijderen
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Deze conceptfactuur verwijderen?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Dit kan niet ongedaan worden gemaakt. De conceptfactuur wordt permanent verwijderd; het factureerbaar saldo van de offerte blijft ongewijzigd.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="factuur-detail-delete-cancel">Annuleer</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={onDelete}
                      className="bg-[#B84B45] hover:bg-[#A03E39]"
                      data-testid="factuur-detail-delete-confirm"
                    >
                      {deleting ? "Bezig…" : "Ja, verwijderen"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    className="h-11 rounded-xl bg-[#2C2C2A] hover:bg-[#1E1E1D] text-white"
                    data-testid="factuur-detail-finalize-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Definitief maken
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Factuur definitief maken?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Zodra de factuur definitief is, kan ze niet meer bewerkt worden. Er wordt een uniek factuurnummer toegekend. Zeker doorgaan?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="factuur-detail-finalize-cancel">Annuleer</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={onFinalize}
                      className="bg-[#2C2C2A] hover:bg-[#1E1E1D]"
                      data-testid="factuur-detail-finalize-confirm"
                    >
                      {finalizing ? "Bezig…" : "Ja, definitief maken"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          {!isDraft && !isCancelled && (
            <>
              {/* v14v Sprint 2B — Creditnota maken vanuit definitieve factuur */}
              {canCreateCreditNote && (
                <Button
                  variant="outline"
                  onClick={() => setCreditNoteOpen(true)}
                  className="h-11 rounded-xl border-[#E9C3C0] text-[#B84B45] hover:bg-[#FDF0EE]"
                  data-testid="factuur-detail-create-credit-note-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Creditnota maken
                </Button>
              )}
              <Badge variant="outline" className="bg-[#F0EFEA] text-[#4F4F4D] border-[#E5E2DC] h-11 px-3 rounded-xl flex items-center">
                <Lock className="w-3.5 h-3.5 mr-1.5" /> Vergrendeld
              </Badge>
            </>
          )}
        </div>
      </div>

      {/* v14o — Mobile-only: Vergrendeld-badge onder titel voor definitieve/betaalde facturen */}
      {!isDraft && !isCancelled && (
        <div className="md:hidden -mt-2" data-testid="factuur-detail-locked-mobile">
          <Badge variant="outline" className="bg-[#F0EFEA] text-[#4F4F4D] border-[#E5E2DC] px-3 rounded-xl inline-flex items-center">
            <Lock className="w-3.5 h-3.5 mr-1.5" /> Vergrendeld
          </Badge>
        </div>
      )}

      {/* Quote snapshot info block (Flow A) */}
      {inv.quote_id && (
        <div className="rounded-2xl bg-[#FAF9F6] border border-[#E5E2DC] p-4 sm:p-5" data-testid="factuur-detail-snapshot">
          <div className="text-[11px] tracking-[0.18em] uppercase text-[#5D7259] font-semibold mb-2">
            Referentie offerte
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-xs text-[#4F4F4D]">Offertenummer</div>
              <div className="font-mono text-[#2C2C2A]">{inv.quote_number_snapshot || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-[#4F4F4D]">Offertedatum</div>
              <div className="text-[#2C2C2A]">
                {inv.quote_date_snapshot ? new Date(inv.quote_date_snapshot).toLocaleDateString("nl-BE") : "—"}
              </div>
            </div>
            <div>
              <div className="text-xs text-[#4F4F4D]">Totaal excl. (offerte)</div>
              <div className="text-[#2C2C2A] font-medium">{fmtEur(inv.quote_total_excl_snapshot)}</div>
            </div>
            <div>
              <div className="text-xs text-[#4F4F4D]">Totaal incl. (offerte)</div>
              <div className="text-[#2C2C2A] font-medium">{fmtEur(inv.quote_total_incl_snapshot)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Line items editor / read-only view */}
      <div className="bg-white border border-[#E5E2DC] rounded-2xl p-4 sm:p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-lg text-[#2C2C2A]">Factuurregels</h2>
          {!isDraft && (
            <span className="text-xs text-[#4F4F4D] flex items-center gap-1">
              <Lock className="w-3 h-3" /> Alleen-lezen
            </span>
          )}
        </div>
        <InvoiceLinesEditor
          lines={lines}
          onChange={setLines}
          readOnly={!isDraft}
          defaultVat={21}
          quoteLinked={!!inv.quote_id}
          quoteNumber={inv.quote_number_snapshot || null}
        />
      </div>

      {/* Totalen + notitie */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5E2DC] rounded-2xl p-4 sm:p-5 space-y-4">
          <div>
            <h3 className="font-heading font-semibold text-base text-[#2C2C2A] mb-3">BTW-regime</h3>
            {isDraft ? (
              <>
                <select
                  value={regime}
                  onChange={(e) => onRegimeChange(e.target.value)}
                  className="flex h-11 w-full rounded-xl border border-[#E5E2DC] bg-white px-3 text-sm"
                  data-testid="factuur-detail-regime-select"
                >
                  {TAX_REGIME_KEYS.map((k) => (
                    <option key={k} value={k}>{TAX_REGIME_LABELS[k]}</option>
                  ))}
                </select>
                <p className="text-[11px] text-[#6E6E6C] mt-1 leading-snug">
                  {TAX_REGIME_HINTS[regime]}
                </p>
                {/* v14r — Mismatch: bewuste afwijking van offerte-BTW */}
                {showRegimeMismatch && (
                  <div
                    className="mt-2 flex items-start gap-2 p-3 rounded-xl bg-[#FFF4E5] border border-[#F0C97E] text-xs text-[#6B4A08]"
                    data-testid="factuur-detail-regime-mismatch"
                  >
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 text-[#B87700] flex-shrink-0" />
                    <span>
                      De offerte gebruikt {quoteVat}% BTW. Deze conceptfactuur is aangepast naar {TAX_REGIME_LABELS[regime]}. Controleer of dit correct is voordat je de factuur definitief maakt.
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-[#2C2C2A]" data-testid="factuur-detail-regime-readonly">
                {TAX_REGIME_LABELS[inv.tax_regime_key] || "Standaard BTW"}
                {inv.legal_notice_snapshot?.status === "pending_verification" && (
                  <div className="mt-2 text-xs text-[#7F4124] bg-[#FDF7F2] border border-[#F0C4A3] rounded-lg px-2.5 py-1.5">
                    ⚠ Wettelijke tekst nog te bevestigen door accountant.
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <h3 className="font-heading font-semibold text-base text-[#2C2C2A] mb-3">Notitie</h3>
            {isDraft ? (
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optionele interne notitie…"
                className="min-h-[100px] rounded-xl"
                data-testid="factuur-detail-notes"
              />
            ) : (
              <div className="text-sm text-[#2C2C2A] whitespace-pre-wrap min-h-[80px]" data-testid="factuur-detail-notes-readonly">
                {inv.notes || <span className="italic text-[#A8A7A0]">Geen notitie</span>}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl bg-[#FAF9F6] border border-[#E5E2DC] p-4 sm:p-5" data-testid="factuur-detail-totals">
          <h3 className="font-heading font-semibold text-base text-[#2C2C2A] mb-3">Totalen</h3>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-[#4F4F4D]">Subtotaal excl.</span>
              <span className="text-[#2C2C2A]">{fmtEur(isDraft ? totals.subtotal : inv.subtotal_excl)}</span>
            </div>
            {Object.entries(isDraft ? totals.buckets : (inv.vat_buckets || {}))
              .filter(([_, v]) => Math.abs(Number(v)) > 0.001)
              .map(([rate, val]) => (
                <div key={rate} className="flex justify-between text-xs">
                  <span className="text-[#4F4F4D]">BTW {rate}%</span>
                  <span className="text-[#4F4F4D]">{fmtEur(val)}</span>
                </div>
              ))}
            <div className="flex justify-between border-t border-[#E5E2DC] pt-2 mt-2">
              <span className="text-[#4F4F4D]">Totaal BTW</span>
              <span className="text-[#2C2C2A]">{fmtEur(isDraft ? totals.total_vat : inv.total_vat)}</span>
            </div>
            <div className="flex justify-between border-t border-[#E5E2DC] pt-2 mt-1">
              <span className="text-[#2C2C2A] font-semibold">Totaal incl.</span>
              <span className="text-[#2C2C2A] font-bold text-lg" data-testid="factuur-detail-total-incl">
                {fmtEur(isDraft ? totals.total_incl : inv.total_incl)}
              </span>
            </div>
          </div>
          {isDraft && (
            <p className="text-[10px] text-[#A8A7A0] mt-3 italic">
              Live voorbeeld — de backend herberekent bij bewaren en definitief maken.
            </p>
          )}
        </div>
      </div>

      {/* v14o — Sticky mobile action bar (alleen mobiel) */}
      {!isCancelled && (
        <>
          {/* Spacer zodat de laatste inhoud niet onder de sticky bar verdwijnt */}
          <div className="md:hidden h-20" aria-hidden="true" />
          <div
            className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-xl border-t border-[#E5E2DC] px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] flex gap-2"
            data-testid="factuur-detail-mobile-actions"
          >
            {isDraft ? (
              <>
                {/* v14r — Concept verwijderen op mobiel: compacte icoon-knop */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-11 w-11 flex-shrink-0 rounded-xl border-[#E9C3C0] text-[#B84B45] hover:bg-[#FDF0EE] p-0"
                      data-testid="factuur-detail-delete-btn-mobile"
                      aria-label="Concept verwijderen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Deze conceptfactuur verwijderen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Dit kan niet ongedaan worden gemaakt. De conceptfactuur wordt permanent verwijderd; het factureerbaar saldo van de offerte blijft ongewijzigd.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="factuur-detail-delete-cancel-mobile">Annuleer</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={onDelete}
                        className="bg-[#B84B45] hover:bg-[#A03E39]"
                        data-testid="factuur-detail-delete-confirm-mobile"
                      >
                        {deleting ? "Bezig…" : "Ja, verwijderen"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button
                  onClick={onSave} disabled={saving}
                  variant="outline"
                  className="flex-1 h-11 rounded-xl border-[#E5E2DC] text-[#2C2C2A] hover:bg-[#F0EFEA]"
                  data-testid="factuur-detail-save-mobile"
                >
                  <Save className="w-4 h-4 mr-1.5" /> {saving ? "Bezig…" : "Bewaar"}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      className="flex-1 h-11 rounded-xl bg-[#2C2C2A] hover:bg-[#1E1E1D] text-white"
                      data-testid="factuur-detail-finalize-btn-mobile"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> Definitief
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Factuur definitief maken?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Zodra de factuur definitief is, kan ze niet meer bewerkt worden. Er wordt een uniek factuurnummer toegekend. Zeker doorgaan?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="factuur-detail-finalize-cancel-mobile">Annuleer</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={onFinalize}
                        className="bg-[#2C2C2A] hover:bg-[#1E1E1D]"
                        data-testid="factuur-detail-finalize-confirm-mobile"
                      >
                        {finalizing ? "Bezig…" : "Ja, definitief maken"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            ) : (
              <>
                {canCreateCreditNote && (
                  <Button
                    variant="outline"
                    onClick={() => setCreditNoteOpen(true)}
                    className="h-11 w-11 flex-shrink-0 rounded-xl border-[#E9C3C0] text-[#B84B45] hover:bg-[#FDF0EE] p-0"
                    data-testid="factuur-detail-create-credit-note-btn-mobile"
                    aria-label="Creditnota maken"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  onClick={async () => {
                    try { await openInvoicePdf(inv.id); }
                    catch (e) {
                      const s = e?.response?.status;
                      if (s === 401) toast.error("Sessie verlopen. Log opnieuw in.");
                      else if (s === 404) toast.error("Factuur niet gevonden.");
                      else toast.error("PDF kon niet worden geopend.");
                    }
                  }}
                  className="flex-1 h-11 rounded-xl bg-[#2C2C2A] hover:bg-[#1E1E1D] text-white"
                  data-testid="factuur-detail-pdf-btn-mobile"
                >
                  <FileDown className="w-4 h-4 mr-1.5" /> PDF openen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    try { downloadInvoicePdf(inv.id); }
                    catch (e) { toast.error("Download mislukt."); }
                  }}
                  className="h-11 w-11 flex-shrink-0 rounded-xl border-[#E5E2DC] text-[#2C2C2A] p-0"
                  data-testid="factuur-detail-download-btn-mobile"
                  aria-label="PDF downloaden"
                  title="PDF downloaden"
                >
                  <FileDown className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </>
      )}

      {/* v14v Sprint 2B — Creditnota-dialog */}
      <CreditnotaVanuitFactuurDialog
        open={creditNoteOpen}
        onOpenChange={setCreditNoteOpen}
        sourceInvoice={inv}
        onCreated={load}
      />
    </div>
  );
}
